import {
  createRouter,
  createWebHistory,
  createMemoryHistory,
} from 'vue-router';
import { auth_store } from '@/stores/modules/auth.store';
import { moduleStorage } from '@/utils/storage';
import { isFederatedModule } from '@/utils/moduleFederation';

const routes = [
  { path: '/', alias: '/init', redirect: { name: 'Discovery' } },
  {
    path: '/apps',
    component: () => import('@/views/Apps/index.vue'),
    children: [
      {
        name: 'Discovery',
        path: 'discovery',
        component: () => import('@/views/Discovery/index.vue'),
      },
      {
        name: 'Apps',
        path: 'my',
        component: () => import('@/views/MyApps/index.vue'),
      },
      {
        name: 'App Config Direct',
        path: 'my/configured/:appCode/:appUuid',
        component: () => import('@/views/MyApps/index.vue'),
      },
      {
        name: 'Other Apps',
        path: 'other-apps',
        component: () => import('@/views/OtherApps/index.vue'),
      },
    ],
  },
  {
    name: 'App Details',
    path: '/:appCode/details',
    component: () => import('@/views/AppDetails/index.vue'),
  },
  {
    path: '/apps/my/:appCode/:appUuid/templates',
    component: () => import('@/views/whatsAppTemplates/Base.vue'),
    children: [
      {
        name: 'WhatsApp Templates Table',
        path: '',
        component: () => import('@/views/whatsAppTemplates/Table.vue'),
        meta: {
          crumb_title: 'WhatsApp.templates.table.crumb_title',
        },
        children: [
          {
            name: 'Create Template',
            path: 'create',
            component: () => import('@/views/whatsAppTemplates/Form.vue'),
            meta: {
              crumb_title: 'WhatsApp.templates.create_form.crumb_title',
            },
          },
          {
            name: 'Edit Template',
            path: 'edit/:templateUuid',
            component: () => import('@/views/whatsAppTemplates/Form.vue'),
            meta: {
              crumb_title: 'WhatsApp.templates.edit_form.crumb_title',
            },
          },
          {
            name: 'Template Details',
            path: 'template-details',
            component: () => import('@/views/TemplateDetails/index.vue'),
            meta: {
              crumb_title: 'WhatsApp.template_details.crumbs.template_details',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/apps/my/:appCode/:appUuid/catalogs',
    component: () => import('@/views/whatsAppCatalogs/Base.vue'),
    children: [
      {
        name: 'WhatsApp Catalogs List',
        path: '',
        component: () => import('@/views/whatsAppCatalogs/List.vue'),
        meta: {
          crumb_title: 'WhatsApp.catalog.list.title',
        },
        children: [
          {
            name: 'WhatsApp Catalog Products',
            path: ':catalogUuid/products',
            component: () =>
              import('@/views/whatsAppCatalogs/CatalogProducts.vue'),
            meta: {
              crumb_title: 'WhatsApp.catalog.products.title',
            },
            props: (route) => {
              return {
                catalogName: route.query.catalogName,
              };
            },
          },
        ],
      },
    ],
  },
  {
    path: '/:project/:flowOrg',
    name: 'externalLogin',
    component: null,
    beforeEnter: (to, from, next) => {
      const { project, flowOrg } = to.params;
      auth_store().selectedProject({ project });
      flowOrg
        ? auth_store().selectedFlowOrg({ flowOrg })
        : auth_store().getFlowOrganization();
      if (to.query.next) {
        next(to.query.next);
      } else {
        next('/');
      }
    },
  },
  {
    path: '/callback',
    name: 'Google Callback',
    component: null,
    beforeEnter: (to, from, next) => {
      const { code } = to.query;
      moduleStorage.setItem('code', code);

      // When opened as a popup (OAuth flow), the host app may be on a
      // different origin than this callback (e.g. when integrations is
      // loaded via Module Federation). localStorage/`storage` events do
      // not cross origins, so we hand the code back through postMessage.
      // The receiver validates `event.origin` and the `source` field.
      if (window.opener) {
        window.opener.postMessage({ source: 'weni-gmail-oauth', code }, '*');
      }

      if (to.query.next) {
        next(to.query.next);
      } else {
        window.close();
      }
    },
  },
];

const history = isFederatedModule
  ? createMemoryHistory() // To isolate routing from parent app
  : createWebHistory('/');

const router = createRouter({
  history,
  routes,
});

router.afterEach((router) => {
  if (isFederatedModule) {
    window.dispatchEvent(
      new CustomEvent('updateRoute', {
        detail: { path: router.path, query: router.query },
      }),
    );
  } else {
    window.parent.postMessage(
      {
        event: 'changePathname',
        pathname: window.location.pathname,
        query: router.query,
      },
      '*',
    );
  }
});

export { routes };
export default router;
